import { useCallback, useEffect, useId, useState } from 'react'
import toast from 'react-hot-toast'
import { pollsAPI } from '../../services/api'
import { apiErrorMessage } from '../../utils/apiError'
import type { Poll } from '../../types/polls'
import ConfirmDialog from '../ConfirmDialog'
import PollCard from './PollCard'

const MIN_OPTIONS = 2
const MAX_OPTIONS = 6

interface Props {
  creatorId: number
  /** Monetizing isn't approved: the form is replaced by this message. */
  blockedMessage?: string | null
}

/**
 * Monetization → Polls: ask your subscribers (or anyone signed in) a
 * question. Polls show on your profile with their counts; each person votes
 * once. Creating one needs an approved application to monetize.
 */
const PollsManager = ({ creatorId, blockedMessage }: Props) => {
  const [polls, setPolls] = useState<Poll[] | null>(null)
  const [question, setQuestion] = useState('')
  const [options, setOptions] = useState<string[]>(['', ''])
  const [subscriberOnly, setSubscriberOnly] = useState(true)
  const [closes, setCloses] = useState('')
  const [saving, setSaving] = useState(false)
  const [closing, setClosing] = useState<Poll | null>(null)
  const [closingBusy, setClosingBusy] = useState(false)
  const formId = useId()

  const load = useCallback(() => {
    pollsAPI
      .list(creatorId, true)
      .then(setPolls)
      .catch((e) => {
        setPolls([])
        toast.error(apiErrorMessage(e, 'Could not load your polls'))
      })
  }, [creatorId])

  useEffect(load, [load])

  const filled = options.map((o) => o.trim()).filter(Boolean)
  const distinct = new Set(filled.map((o) => o.toLowerCase())).size === filled.length
  const valid = question.trim().length > 0 && filled.length >= MIN_OPTIONS && distinct
  const today = new Date().toISOString().slice(0, 10)

  const create = (e: React.FormEvent) => {
    e.preventDefault()
    if (!valid) return
    setSaving(true)
    pollsAPI
      .create({
        question: question.trim(),
        options: filled,
        subscriber_only: subscriberOnly,
        // The end of the chosen day, UTC.
        ...(closes ? { closes_at: `${closes}T23:59:59Z` } : {}),
      })
      .then(() => {
        toast.success('Poll posted')
        setQuestion('')
        setOptions(['', ''])
        setCloses('')
        load()
      })
      .catch((err) => toast.error(apiErrorMessage(err, 'Could not post the poll')))
      .finally(() => setSaving(false))
  }

  const close = () => {
    if (!closing) return
    setClosingBusy(true)
    pollsAPI
      .close(closing.id)
      .then(() => {
        toast.success('Poll closed')
        setClosing(null)
        load()
      })
      .catch((err) => toast.error(apiErrorMessage(err, 'Could not close the poll')))
      .finally(() => setClosingBusy(false))
  }

  const open = (polls ?? []).filter((p) => p.is_active)
  const past = (polls ?? []).filter((p) => !p.is_active)

  return (
    <div className="space-y-6" data-testid="polls-manager">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Polls</h2>
        <p className="mt-1 text-sm text-gray-600">
          Ask your audience a question. Polls show on your profile with their vote counts, which everyone can see;
          each person votes once, and you can&apos;t vote on your own.
        </p>
      </div>

      {blockedMessage ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="polls-blocked">
          {blockedMessage}
        </p>
      ) : (
        <form onSubmit={create} className="space-y-3 rounded-lg border border-gray-200 bg-white p-4" aria-labelledby={`${formId}-h`}>
          <h3 id={`${formId}-h`} className="font-semibold text-gray-900">
            New poll
          </h3>
          <div>
            <label htmlFor={`${formId}-q`} className="block text-sm font-medium text-gray-700">
              Question
            </label>
            <input
              id={`${formId}-q`}
              value={question}
              maxLength={500}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. Which coin should I cover next week?"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-gray-700">Options</legend>
            {options.map((option, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  aria-label={`Option ${i + 1}`}
                  value={option}
                  maxLength={200}
                  onChange={(e) => setOptions((prev) => prev.map((o, k) => (k === i ? e.target.value : o)))}
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                />
                {options.length > MIN_OPTIONS && (
                  <button
                    type="button"
                    onClick={() => setOptions((prev) => prev.filter((_, k) => k !== i))}
                    aria-label={`Remove option ${i + 1}`}
                    className="shrink-0 text-sm text-gray-500 hover:text-gray-700"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            {options.length < MAX_OPTIONS && (
              <button
                type="button"
                onClick={() => setOptions((prev) => [...prev, ''])}
                className="text-sm text-primary-600 hover:underline"
              >
                Add an option
              </button>
            )}
            {!distinct && <p className="text-xs text-danger-600">Each option must be different.</p>}
          </fieldset>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={subscriberOnly} onChange={(e) => setSubscriberOnly(e.target.checked)} />
              Only subscribers can vote
            </label>
            <div>
              <label htmlFor={`${formId}-closes`} className="block text-sm font-medium text-gray-700">
                Closes (optional)
              </label>
              <input
                id={`${formId}-closes`}
                type="date"
                min={today}
                value={closes}
                onChange={(e) => setCloses(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={!valid || saving}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Posting…' : 'Post poll'}
          </button>
        </form>
      )}

      {polls === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : polls.length === 0 ? (
        <p className="text-sm text-gray-500">No polls yet.</p>
      ) : (
        <>
          {open.length > 0 && (
            <section aria-label="Open polls" className="space-y-3">
              <h3 className="font-semibold text-gray-900">Open</h3>
              {open.map((p) => (
                <PollCard
                  key={p.id}
                  poll={p}
                  action={
                    <button
                      type="button"
                      onClick={() => setClosing(p)}
                      className="shrink-0 rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  }
                />
              ))}
            </section>
          )}
          {past.length > 0 && (
            <section aria-label="Closed polls" className="space-y-3">
              <h3 className="font-semibold text-gray-900">Closed</h3>
              {past.map((p) => (
                <PollCard key={p.id} poll={p} />
              ))}
            </section>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={!!closing}
        title="Close this poll?"
        message={
          <>
            <strong>{closing?.question}</strong> stops taking votes. Its results stay visible.
          </>
        }
        confirmLabel="Close poll"
        confirming={closingBusy}
        onConfirm={close}
        onCancel={() => setClosing(null)}
      />
    </div>
  )
}

export default PollsManager
